import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import {
  Users, UserPlus, Target, DollarSign, TrendingUp,
  Calendar, FileText, Activity, Award, BarChart3
} from 'lucide-react';
import FilterBuilder from './FilterBuilder';

const ICON_OPTIONS = [
  { id: 'Users', label: 'Users', Icon: Users },
  { id: 'UserPlus', label: 'User Plus', Icon: UserPlus },
  { id: 'Target', label: 'Target', Icon: Target },
  { id: 'DollarSign', label: 'Dollar', Icon: DollarSign },
  { id: 'TrendingUp', label: 'Trending Up', Icon: TrendingUp },
  { id: 'Calendar', label: 'Calendar', Icon: Calendar },
  { id: 'FileText', label: 'File', Icon: FileText },
  { id: 'Activity', label: 'Activity', Icon: Activity },
  { id: 'Award', label: 'Award', Icon: Award },
  { id: 'BarChart3', label: 'Chart', Icon: BarChart3 }
];

const COLOR_OPTIONS = [
  { id: 'blue', label: 'Blue', bg: 'bg-blue-500', text: 'text-blue-600' },
  { id: 'green', label: 'Green', bg: 'bg-green-500', text: 'text-green-600' },
  { id: 'purple', label: 'Purple', bg: 'bg-purple-500', text: 'text-purple-600' },
  { id: 'red', label: 'Red', bg: 'bg-red-500', text: 'text-red-600' },
  { id: 'yellow', label: 'Yellow', bg: 'bg-yellow-500', text: 'text-yellow-600' },
  { id: 'indigo', label: 'Indigo', bg: 'bg-indigo-500', text: 'text-indigo-600' }
];

const StatCardEditor = ({ entityName, config, onChange }) => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (entityName) {
      loadFields();
    }
  }, [entityName]);

  // Initialize config with default aggregation if not set
  useEffect(() => {
    if (!config.aggregation || !config.aggregation.function) {
      onChange({
        ...config,
        aggregation: {
          function: 'COUNT'
        }
      });
    }
  }, []);

  const loadFields = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/entity-metadata/${entityName}/fields`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load fields');
      }

      const data = await response.json();
      // Filter numeric fields for SUM aggregation
      const numericFields = (data.data || []).filter(f =>
        ['integer', 'decimal', 'number', 'currency', 'int', 'float', 'double'].includes(f.type?.toLowerCase())
      );
      setFields(numericFields);
    } catch (error) {
      console.error('Error loading fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAggregationChange = (key, value) => {
    onChange({
      ...config,
      aggregation: {
        ...config.aggregation,
        [key]: value
      }
    });
  };

  const handleChange = (key, value) => {
    onChange({
      ...config,
      [key]: value
    });
  };

  const aggregationFunction = config.aggregation?.function || 'COUNT';
  const selectedIcon = config.icon || 'Users';
  const selectedColor = config.color || 'blue';

  return (
    <div className="space-y-4">
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Stat Card Configuration
        </h3>

        {/* Aggregation Function */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Aggregation Function
          </label>
          <select
            value={aggregationFunction}
            onChange={(e) => handleAggregationChange('function', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="COUNT">Count Records</option>
            <option value="SUM">Sum Field Value</option>
          </select>
        </div>

        {/* Field Selection (for SUM) */}
        {aggregationFunction === 'SUM' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Field to Sum
            </label>
            {loading ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading fields...</div>
            ) : fields.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                No numeric fields available
              </div>
            ) : (
              <select
                value={config.aggregation?.field || ''}
                onChange={(e) => handleAggregationChange('field', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="">Select field...</option>
                {fields.map(field => (
                  <option key={field.name} value={field.name}>
                    {field.label} ({field.type})
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Icon Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Icon
          </label>
          <div className="grid grid-cols-5 gap-2">
            {ICON_OPTIONS.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => handleChange('icon', id)}
                className={`p-3 rounded-lg border-2 transition-all ${
                  selectedIcon === id
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                title={label}
              >
                <Icon className="w-5 h-5 mx-auto" />
              </button>
            ))}
          </div>
        </div>

        {/* Color Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Color
          </label>
          <div className="grid grid-cols-6 gap-2">
            {COLOR_OPTIONS.map(({ id, label, bg }) => (
              <button
                key={id}
                onClick={() => handleChange('color', id)}
                className={`h-10 rounded-lg border-2 transition-all ${bg} ${
                  selectedColor === id
                    ? 'border-gray-900 dark:border-white ring-2 ring-offset-2 ring-teal-500'
                    : 'border-transparent'
                }`}
                title={label}
              />
            ))}
          </div>
        </div>

        {/* Format */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Display Format
          </label>
          <select
            value={config.format || 'number'}
            onChange={(e) => handleChange('format', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="number">Number</option>
            <option value="currency">Currency</option>
            <option value="percentage">Percentage</option>
            <option value="decimal">Decimal (2 places)</option>
          </select>
        </div>

        {/* Label Override */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Custom Label (optional)
          </label>
          <input
            type="text"
            value={config.label || ''}
            onChange={(e) => handleChange('label', e.target.value)}
            placeholder="e.g., Total Customers"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
          />
        </div>

        {/* Drilldown URL */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Drilldown URL (optional)
          </label>
          <input
            type="text"
            value={config.drilldownUrl || ''}
            onChange={(e) => handleChange('drilldownUrl', e.target.value)}
            placeholder="e.g., /customer"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            URL to navigate to when the stat card is clicked
          </p>
        </div>

        {/* Filters */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <FilterBuilder
            entityName={entityName}
            filters={config.filters || []}
            onChange={(filters) => handleChange('filters', filters)}
          />
        </div>
      </div>
    </div>
  );
};

export default StatCardEditor;

